import CircleIcon from '@mui/icons-material/Circle'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import { Box, Button, Skeleton, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { format } from 'date-fns'
import { useMsal } from '@azure/msal-react'
import { UpdateStoreHelper } from '../source/StoreHelper'
import { useDispatch, useSelector } from 'react-redux'
import { DayCalendarSkeleton, PickerDay } from '@mui/x-date-pickers'
import { Trans, useTranslation } from 'react-i18next'
import axios from 'axios'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { setLoadingCalendar, setRequireCalendarReload } from '../source/slice/LoadingDateSlice'
import { setProjects } from '../source/slice/ProjectsSlice'
import { ICalendar } from '../interface/ICalendar'
import { formatTimeAsLocal } from '../utils/functions/timeUtils'
import { ITimecard } from '../interface/ISelectedDate'
import { UpdateStoreHelperJournal } from '../source/StoreHelperJournal'
import { IUser } from '../interface/IUser'
import { IJournal } from '../interface/IJournal'
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { getAuthConfig } from '../utils/authApi'
import TodayIcon from '@mui/icons-material/Today'
import ChangedDataWarning, { useUnsavedChanges } from './Warnings/ChangedDataWarning'
import { DecemberWarning } from './Warnings/DecemberWarning'
import WarningDialog from './Warnings/WarningDialog'
import { MonthlyClosing } from './Warnings/MonthlyClosingWarning'
import { setStateCalendarDates } from '@/source/slice/CalendarDatesSlice'
import { IOrdersRef } from '@/interface/IOrder'
import { setOrders } from '@/source/slice/OrderSlice'
import { injectedEndpoints } from '@/api/InjectedEndpoints'
import { store } from '../source/Store'
import noCapacityIcon from '../img/nocapacity.svg'

type ServerDayProps = {
    day: Date
    outsideCurrentMonth: boolean
    getStatusObj: (other: any, outsideCurrentMonth: any, day: any) => React.ReactElement
    getStatus: (day: Date) => ICalendar | null
    hasTimecard: boolean | undefined
    [key: string]: any
}

function ServerDay({ day, outsideCurrentMonth, getStatusObj, getStatus, hasTimecard, ...other }: Readonly<ServerDayProps>) {
    const { t, i18n } = useTranslation()
    const theme = useTheme()
    const dateInformation = getStatus(day)

    const statusIconProps: Record<string, { color?: React.ComponentProps<typeof CircleIcon>['color']; style?: React.CSSProperties; outlined?: boolean }> = {
        SAVED: { style: { color: '#ddf0dd' } },
        RELEASED: { color: 'success' },
        ABSENCE: { style: { color: 'rgb(107, 0, 138)' } },
        HOLIDAY: { style: { color: '#ECDEF1' } },
        WEEKEND: { color: 'disabled' },
        OPEN: { style: { color: 'rgb(244, 244, 244)' } },
    }

    const status = dateInformation?.status || 'OPEN'
    const iconProps = statusIconProps[status]

    const formattedDay = i18n.language === 'de' ? format(day, 'EEEE, dd.MM.yyyy', { locale: de }) : format(day, 'EEEE, MM/dd/yyyy', { locale: enUS })

    const tooltipContent = (
        <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.25 }}>
                {formattedDay}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {iconProps?.outlined ? <FiberManualRecordOutlinedIcon color={iconProps.color} sx={{ fontSize: 12 }} /> : <CircleIcon color={iconProps?.color} style={iconProps?.style} sx={{ fontSize: 12 }} />}
                <Typography variant="caption">
                    <Trans i18nKey="calendar.tooltipStatus" values={{ status: t(`calendar.${status.toLowerCase()}`) }} components={{ bold: <strong /> }} />
                </Typography>
            </Box>
            {dateInformation?.from && dateInformation?.to && (
                <Typography variant="caption" component="div">
                    <Trans i18nKey="calendar.tooltipPresence" values={{ from: formatTimeAsLocal(dateInformation.from, dateInformation.date), to: formatTimeAsLocal(dateInformation.to, dateInformation.date) }} components={{ bold: <strong /> }} />
                </Typography>
            )}
            {!hasTimecard && (dateInformation?.amount ?? 0) > 0 && (
                <Typography variant="caption" component="div">
                    <Trans i18nKey="calendar.tooltipAmount" values={{ amount: (dateInformation?.amount ?? 0).toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} components={{ bold: <strong /> }} />
                </Typography>
            )}
            {dateInformation?.onCall && (
                <Typography variant="caption" component="div">
                    {t('calendar.tooltipOnCall')}
                </Typography>
            )}
        </Box>
    )

    return (
        <Tooltip
            arrow
            title={tooltipContent}
            slotProps={{
                tooltip: { sx: { bgcolor: alpha(theme.palette.grey[700], 1), '& .MuiTooltip-arrow': { color: alpha(theme.palette.grey[700], 1) } } },
            }}
        >
            {getStatusObj(other, outsideCurrentMonth, day)}
        </Tooltip>
    )
}

function renderDay(getStatusObj: ServerDayProps['getStatusObj'], getStatus: ServerDayProps['getStatus'], hasTimecard: boolean | undefined) {
    return function DayComponent(props: any) {
        return <ServerDay {...props} getStatusObj={getStatusObj} getStatus={getStatus} hasTimecard={hasTimecard} />
    }
}

const Calendar = () => {
    const { instance, accounts } = useMsal()
    const { t, i18n } = useTranslation()
    const theme = useTheme()

    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const loadingCalendar: boolean = useSelector((state: any) => state.loadingDate?.loadingCalendar)
    const requireCalendarReload: boolean = useSelector((state: any) => state.loadingDate?.requireCalendarReload)
    const weekStart: string = useSelector((state: any) => state.weekStart?.value ?? 'monday')
    const dispatch = useDispatch()

    const { checkUnsaved, resetBaseline } = useUnsavedChanges()
    const [pendingDate, setPendingDate] = useState<Date | null>(null)

    const noProjectsWarningShownRef = useRef(false)
    const [noProjectsDialogOpen, setNoProjectsDialogOpen] = useState(false)

    const registrationWarningShownForDateRef = useRef<string | null>(null)
    const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false)
    const [registrationDialogTextKey, setRegistrationDialogTextKey] = useState<string | null>(null)

    const getDefaultSelectedDate = (): Date => {
        const defaultDate = selectedDate.header?.mserp_timeprofiledate ?? selectedJournal.header?.date

        return defaultDate ? new Date(defaultDate) : new Date()
    }

    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const canShowChangedDataWarning = selectedDate?.header?.status === 'OPEN' || selectedDate?.header?.status === 'SAVED'
    const selectedJournal: IJournal = useSelector((state: any) => state.journal?.value)
    const [selectedDateValue, setSelectedDateValue] = useState<Date>(getDefaultSelectedDate())
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date(selectedDateValue.getFullYear(), selectedDateValue.getMonth(), 1))
    const [calendarDates, setCalendarDates] = useState<ICalendar[]>([])

    const user: IUser | undefined = useSelector((state: any) => state.user?.value)
    const hasTimecard = user?.worker?.mserp_usetimecard
    const scope: string = process.env.REACT_APP_SCOPE || ''

    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    useEffect(() => {
        if (!user?.worker) return

        const formattedDate = format(selectedDateValue, 'yyyy-MM-dd')

        const getProjects = (selectedDate: string) => {
            try {
                const projectsPromise = store.dispatch(injectedEndpoints.endpoints.getProjects.initiate(selectedDate, { forceRefetch: false })).unwrap()
                projectsPromise
                    .then((rawProjects) => {
                        const projects = Array.isArray(rawProjects) ? rawProjects : []
                        dispatch(setProjects(projects))

                        if (projects.length === 0 && !noProjectsWarningShownRef.current) {
                            setNoProjectsDialogOpen(true)
                            noProjectsWarningShownRef.current = true
                        }
                    })
                    .catch((error) => {
                        console.log(error)
                    })
            } catch (error) {
                console.log(error)
            }
        }

        getProjects(formattedDate)

        if (user?.worker?.mserp_usetimecard) {
            UpdateStoreHelper(formattedDate, true)
        } else {
            UpdateStoreHelperJournal(formattedDate, true)
        }
    }, [selectedDateValue, user?.worker])

    useEffect(() => {
        if (!user?.worker) return
        if (loadingDate) return

        const selectedDateKey = format(selectedDateValue, 'yyyy-MM-dd')
        const loadedDateRaw = selectedDate?.header?.mserp_timeprofiledate ?? selectedJournal.header?.date
        const loadedDateKey = loadedDateRaw ? format(new Date(loadedDateRaw), 'yyyy-MM-dd') : null
        if (loadedDateKey !== selectedDateKey) return

        const parseDateOnly = (value?: string) => {
            if (!value) return null
            const parsed = new Date(value)
            if (Number.isNaN(parsed.getTime())) return null
            return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
        }

        const selectedDateOnly = new Date(selectedDateValue.getFullYear(), selectedDateValue.getMonth(), selectedDateValue.getDate())

        const fromValue = selectedDate?.header?.registrationPermittedFromDate ?? selectedJournal.header?.registrationPermittedFromDate
        const toValue = selectedDate?.header?.registrationPermittedToDate ?? selectedJournal.header?.registrationPermittedToDate

        const fromDate = parseDateOnly(fromValue)
        const toDate = parseDateOnly(toValue)

        if (!fromDate || !toDate) {
            setRegistrationDialogOpen(false)
            registrationWarningShownForDateRef.current = null
            setRegistrationDialogTextKey(null)
            return
        }

        const isOutside = selectedDateOnly < fromDate //|| selectedDateOnly > toDate
        const dateKey = format(selectedDateValue, 'yyyy-MM-dd')

        if (!isOutside || (selectedDate.header?.isTransferred && selectedJournal.header?.mserp_posted) || selectedDate.header?.mserp_timeprofileid === 'BLOCK' || selectedDate.header?.specialDayId === 'REDUCE') {
            setRegistrationDialogOpen(false)
            registrationWarningShownForDateRef.current = null
            setRegistrationDialogTextKey(null)
            return
        }

        if (registrationWarningShownForDateRef.current === dateKey) return

        const isMA = user?.worker?.mserp_usetimecard
        const isANU = user?.worker?.mserp_employmentcategory === 'ANÜ'

        if (isMA) setRegistrationDialogTextKey('main.registrationPermittedPeriodWarningMA')
        else if (isANU) setRegistrationDialogTextKey('main.registrationPermittedPeriodWarningANU')
        else setRegistrationDialogTextKey('main.registrationPermittedPeriodWarningFAK')

        setRegistrationDialogOpen(true)
        registrationWarningShownForDateRef.current = dateKey
    }, [selectedDateValue, selectedDate, user, loadingDate])

    const getSelectedDate = useCallback(() => {
        dispatch(setLoadingCalendar(true))
        dispatch(setRequireCalendarReload(false))
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                axios
                    .get(`${process.env.REACT_APP_BACKEND_URL}/Calendar`, {
                        ...headers,
                        data: {},
                        params: {
                            date: format(selectedMonth, 'yyyy-MM-dd'),
                        },
                    })
                    .then((response) => {
                        setCalendarDates(response.data)
                        dispatch(setStateCalendarDates(response.data))
                    })
                    .catch((error) => {
                        console.log(error)
                    })
                    .finally(() => {
                        dispatch(setLoadingCalendar(false))
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                if ((error as any)?.errorCode !== 'timed_out') {
                    console.log(error)
                }
                dispatch(setLoadingCalendar(false))
            })
    }, [selectedMonth])

    const getOrders = async () => {
        try {
            const date = format(new Date(), 'yyyy-MM-dd')
            const orders: IOrdersRef[] = await store.dispatch(injectedEndpoints.endpoints.getOrders.initiate(date, { forceRefetch: false })).unwrap()
            store.dispatch(setOrders(orders))
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        getSelectedDate()
    }, [selectedMonth])

    useEffect(() => {
        if (user?.worker && user?.worker?.mserp_employmentcategory) {
            if (user?.worker?.mserp_employmentcategory === 'ANÜ' || user?.worker?.mserp_employmentcategory === 'FAK') {
                getOrders()
            }
        }
    }, [selectedDateValue])

    useEffect(() => {
        if (requireCalendarReload) {
            getSelectedDate()
        }
    }, [requireCalendarReload])

    useEffect(() => {
        if (!canShowChangedDataWarning && pendingDate !== null) {
            setPendingDate(null)
        }
    }, [canShowChangedDataWarning, pendingDate])

    const getStatus = (date: Date) => {
        const matchingDates = calendarDates.filter((d) => isSameCalendarDate(date, new Date(d.date)))

        if (matchingDates.length > 0) {
            return matchingDates[0]
        } else {
            return null
        }
    }

    const isSameCalendarDate = (a: Date, b: Date) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()

    const getStatusObj = (other: any, outsideCurrentMonth: any, day: any) => {
        const isSelected = other.selected
        const colors = {
            OPEN: {
                bgcolor: theme.palette.action.active,
                bordercolor: theme.palette.success.main,
                color: 'white',
            },
            SAVED: {
                bgcolor: '#ddf0dd',
                bordercolor: '#ddf0dd',
                color: theme.palette.success.main,
            },
            RELEASED: {
                bgcolor: theme.palette.success.main,
                bordercolor: theme.palette.success.main,
                color: 'white',
            },
            ABSENCE: {
                bgcolor: 'rgb(107, 0, 138)',
                bordercolor: 'rgb(107, 0, 138)',
                color: 'white',
            },
            HOLIDAY: {
                bgcolor: '#ECDEF1',
                bordercolor: '#ECDEF1',
                color: 'rgb(107, 0, 138)',
            },
            WEEKEND: {
                bgcolor: theme.palette.action.selected,
                bordercolor: theme.palette.action.selected,
                color: 'white',
            },
            ERROR: {
                color: theme.palette.error.main,
            },
        }
        const dateInformation = getStatus(day)

        if (dateInformation) {
            const dayStatus = dateInformation.status
            switch (dayStatus) {
                case 'SAVED':
                    return (
                        <PickerDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.SAVED.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : `2px solid ${colors.SAVED.bordercolor}`,
                                color: `${dateInformation.noCapacity && hasTimecard ? colors.ERROR.color : colors.SAVED.color} !important`,
                                boxShadow: dateInformation.onCall ? `0 0 0 2px white inset` : undefined,
                            }}
                        />
                    )

                case 'RELEASED':
                    return (
                        <PickerDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.RELEASED.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : `2px solid ${colors.RELEASED.bordercolor}`,
                                color: `${dateInformation.noCapacity && hasTimecard ? colors.ERROR.color : colors.RELEASED.color} !important`,
                                boxShadow: dateInformation.onCall ? `0 0 0 2px white inset` : undefined,
                            }}
                        />
                    )

                case 'ABSENCE':
                    return (
                        <PickerDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.ABSENCE.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                borderColor: isSelected ? undefined : colors.ABSENCE.bordercolor,
                                color: `${colors.ABSENCE.color} !important`,
                            }}
                        />
                    )

                case 'HOLIDAY':
                    return (
                        <PickerDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.HOLIDAY.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                borderColor: isSelected ? undefined : colors.HOLIDAY.bordercolor,
                                color: `${colors.HOLIDAY.color} !important`,
                            }}
                        />
                    )

                case 'OPEN':
                    if (day.getDay() === 0 || day.getDay() === 6)
                        return (
                            <PickerDay
                                {...other}
                                outsideCurrentMonth={outsideCurrentMonth}
                                day={day}
                                sx={{
                                    bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                    borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                                }}
                            />
                        )
                    else
                        return (
                            <PickerDay
                                {...other}
                                outsideCurrentMonth={outsideCurrentMonth}
                                day={day}
                                sx={
                                    dateInformation.noCapacity && hasTimecard
                                        ? { color: colors.ERROR.color, border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined }
                                        : { border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined }
                                }
                            />
                        )

                default:
                    return (
                        <PickerDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                            }}
                        />
                    )
            }
        }
        if (day.getDay() === 0 || day.getDay() === 6)
            return (
                <PickerDay
                    {...other}
                    outsideCurrentMonth={outsideCurrentMonth}
                    day={day}
                    sx={{
                        bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                        border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                        borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                    }}
                />
            )
        else return <PickerDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} sx={{ border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined }} />
    }

    return (
        <>
            <Box sx={{ textAlign: 'center', padding: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={{ ...(i18n.language === 'de' ? de : enUS), options: { ...(i18n.language === 'de' ? de : enUS).options, weekStartsOn: weekStart === 'monday' ? 1 : 0 } }}>
                    {selectedDate?.header?.mserp_timeprofiledate || selectedJournal.header?.mserp_journalname || selectedJournal.header?.date ? (
                        <>
                            <DateCalendar
                                views={['day', 'month', 'day']}
                                onChange={(value: any) => {
                                    if (user?.worker?.mserp_usetimecard) {
                                        if (canShowChangedDataWarning) {
                                            const changed = checkUnsaved()
                                            if (changed) setPendingDate(value)
                                            else setSelectedDateValue(value)
                                        } else {
                                            setPendingDate(null)
                                            setSelectedDateValue(value)
                                        }
                                    } else {
                                        setSelectedDateValue(value)
                                    }
                                }}
                                value={selectedDateValue}
                                disabled={loadingDate || loadingCalendar}
                                slots={{
                                    day: renderDay(getStatusObj, getStatus, hasTimecard),
                                }}
                                showDaysOutsideCurrentMonth
                                onMonthChange={(month: Date) => setSelectedMonth(month)}
                                sx={{
                                    width: 280,
                                }}
                            />
                            <ChangedDataWarning
                                open={pendingDate !== null}
                                onConfirm={() => {
                                    if (pendingDate) {
                                        setSelectedDateValue(pendingDate)
                                        setPendingDate(null)
                                        resetBaseline()
                                    }
                                }}
                                onCancel={() => setPendingDate(null)}
                            />
                        </>
                    ) : (
                        <>
                            <Skeleton variant="rounded" sx={{ width: 280, height: 40, my: '8px' }} />
                            <Skeleton variant="rounded" sx={{ width: 280, height: 24, my: '8px' }} />
                            <DayCalendarSkeleton sx={{ width: 280, height: 240, mt: '12px' }} />
                        </>
                    )}
                </LocalizationProvider>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Button variant="text" onClick={() => setSelectedDateValue(new Date())} startIcon={<TodayIcon />} disabled={loadingDate || loadingCalendar}>
                    {t('calendar.today')}
                </Button>
            </Box>

            <Box sx={{ textAlign: 'center', padding: 0, fontSize: '14px', lineHeight: 1.4 }}>
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', mr: 1 }}>
                    <FiberManualRecordOutlinedIcon color="disabled" sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.open')}
                </Box>
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', mr: 1 }}>
                    <FiberManualRecordOutlinedIcon color="primary" sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.selected')}
                </Box>
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <CircleIcon style={{ color: '#ddf0dd' }} sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.saved')}
                </Box>
                <br />
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', mr: 1 }}>
                    <CircleIcon color="success" sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.released')}
                </Box>
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <CircleIcon color="disabled" sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.weekend')}
                </Box>
                <br />
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', mr: 1 }}>
                    <CircleIcon style={{ color: '#ECDEF1' }} sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.holiday')}
                </Box>
                <Box component="div" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <CircleIcon style={{ color: 'rgb(107, 0, 138)' }} sx={{ fontSize: 12, mx: 0.5 }} />
                    {t('calendar.absence')}
                </Box>
                {hasTimecard && (
                    <>
                        <br />
                        <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                            <Tooltip title={t('calendar.nocapacityinfo')}>
                                <span>
                                    <img src={noCapacityIcon} alt="No Capacity" style={{ height: 16, verticalAlign: 'middle', marginRight: '8px' }} />
                                    {t('calendar.nocapacity')}
                                </span>
                            </Tooltip>
                        </Box>
                    </>
                )}
            </Box>

            <MonthlyClosing />
            <DecemberWarning notInt={!user?.worker?.mserp_usetimecard || user?.worker?.mserp_employmentcategory === 'ANÜ'} />

            <WarningDialog
                open={noProjectsDialogOpen}
                onClose={() => setNoProjectsDialogOpen(false)}
                title={t('main.noProjectsWarningTitle')}
                text={t('main.noProjectsWarning')}
                buttonText={t('spainAbsenceWarning.confirm')}
                dialogTitleId="no-projects-warning-title"
            />

            <WarningDialog
                open={registrationDialogOpen && !!registrationDialogTextKey}
                onClose={() => setRegistrationDialogOpen(false)}
                title={t('main.registrationPermittedPeriodWarningTitle')}
                text={registrationDialogTextKey ? t(registrationDialogTextKey) : ''}
                buttonText={t('spainAbsenceWarning.confirm')}
                dialogTitleId="registration-period-warning-title"
            />
        </>
    )
}

export default Calendar
